/**
 * Project Name: Brainsie eCommerce v4
 * Component: Component Renderer
 * Description: Renders individual components within sections
 * Author: Brainsie Team
 * Date Created: October 24, 2025
 */

import React from "react";
import { Box } from "@mui/material";
import { getThemeAwareStyles } from "./utils/themeUtils";

// Import component types
import DefaultSquareImage from "./components/DefaultSquareImage";
import DefaultParagraph from "./components/DefaultParagraph";
import DefaultList from "./components/DefaultList";
import LearnMoreCTA from "./components/LearnMoreCTA";
import SimpleCard from "./components/SimpleCard";
import SimpleTab from "./components/SimpleTab";
import FullVerticalImage from "./components/FullVerticalImage";
import IconCard from "./components/IconCard";
import BlogCard from "./components/BlogCard";

// Component type mapping by Component_Master_Code
const COMPONENT_TYPES = {
  COMP_001: DefaultSquareImage, // Default Square Image
  COMP_002: DefaultParagraph, // Default Paragraph
  COMP_003: DefaultList, // Default List
  COMP_004: LearnMoreCTA, // Learn More CTA
  COMP_005: SimpleCard, // Simple Card
  COMP_006: SimpleTab, // Simple Tab
  COMP_007: FullVerticalImage, // Full Vertical Image
  COMP_008: IconCard, // Icon Card
  COMP_009: BlogCard, // Blog Card
};

export default function ComponentRenderer({
  component,
  section,
  darkMode,
  theme,
}) {
  // Get component settings with defaults
  const componentSettings = component.Component_Settings
    ? JSON.parse(component.Component_Settings)
    : {
        size: "medium",
        variant: "contained",
        color: "primary",
        fullWidth: false,
        disabled: false,
        fontSize: "",
        fontWeight: "",
        textAlign: "left",
        backgroundColor: "",
        textColor: "",
        borderRadius: "",
        padding: "",
        margin: "",
        elevation: 0,
        customStyles: "",
      };

  // Get theme-aware styles
  const themeStyles = getThemeAwareStyles(darkMode, theme, componentSettings);

  // Helper function to render List Content as JSON array
  const renderListContent = (listContent) => {
    if (!listContent) return null;

    try {
      const items = JSON.parse(listContent);
      if (Array.isArray(items) && items.length > 0) {
        return (
          <ul
            style={{
              margin: "16px 0",
              paddingLeft: "20px",
              color: componentSettings.textColor || themeStyles.primaryText,
              fontSize: componentSettings.fontSize || "inherit",
              fontWeight: componentSettings.fontWeight || "normal",
              textAlign: componentSettings.textAlign || "left",
            }}
          >
            {items.map((item, index) => (
              <li key={index} style={{ marginBottom: "8px" }}>
                {item}
              </li>
            ))}
          </ul>
        );
      }
    } catch (error) {
      // Fallback for non-JSON content
      return (
        <ul style={{ margin: "16px 0", paddingLeft: "20px" }}>
          <li>{listContent}</li>
        </ul>
      );
    }
    return null;
  };

  // Helper function to render CTA buttons
  const renderCTAButtons = (primaryCTA, secondaryCTA) => {
    const buttons = [];

    if (primaryCTA) {
      buttons.push(
        <button
          key="primary"
          style={{
            backgroundColor:
              componentSettings.backgroundColor || theme.palette.primary.main,
            color:
              componentSettings.textColor || theme.palette.primary.contrastText,
            border: "none",
            borderRadius: componentSettings.borderRadius || "4px",
            padding: componentSettings.padding || "8px 16px",
            margin: componentSettings.margin || "8px 8px 8px 0",
            fontSize: componentSettings.fontSize || "14px",
            fontWeight: componentSettings.fontWeight || "500",
            cursor: componentSettings.disabled ? "not-allowed" : "pointer",
            opacity: componentSettings.disabled ? 0.6 : 1,
            textAlign: componentSettings.textAlign || "center",
            minWidth: componentSettings.fullWidth ? "100%" : "auto",
          }}
          disabled={componentSettings.disabled}
        >
          {primaryCTA}
        </button>
      );
    }

    if (secondaryCTA) {
      buttons.push(
        <button
          key="secondary"
          style={{
            backgroundColor: "transparent",
            color: componentSettings.textColor || theme.palette.text.primary,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: componentSettings.borderRadius || "4px",
            padding: componentSettings.padding || "8px 16px",
            margin: componentSettings.margin || "8px 8px 8px 0",
            fontSize: componentSettings.fontSize || "14px",
            fontWeight: componentSettings.fontWeight || "400",
            cursor: componentSettings.disabled ? "not-allowed" : "pointer",
            opacity: componentSettings.disabled ? 0.6 : 1,
            textAlign: componentSettings.textAlign || "center",
            minWidth: componentSettings.fullWidth ? "100%" : "auto",
          }}
          disabled={componentSettings.disabled}
        >
          {secondaryCTA}
        </button>
      );
    }

    return buttons.length > 0 ? (
      <Box
        sx={{ mt: 2, display: "flex", flexWrap: "wrap", alignItems: "center" }}
      >
        {buttons}
      </Box>
    ) : null;
  };

  // Get the appropriate component type
  let ComponentType = COMPONENT_TYPES[component.Component_Master_Code];

  // Fallback: check by Component_Name if code-based lookup fails
  if (!ComponentType && component.Component_Name) {
    switch (component.Component_Name) {
      case "Default Square Image":
        ComponentType = DefaultSquareImage;
        break;
      case "Default Paragraph":
        ComponentType = DefaultParagraph;
        break;
      case "Default List":
        ComponentType = DefaultList;
        break;
      case "Learn More CTA":
        ComponentType = LearnMoreCTA;
        break;
      case "Simple Card":
        ComponentType = SimpleCard;
        break;
      case "Simple Tab":
        ComponentType = SimpleTab;
        break;
      case "Full Vertical Image":
        ComponentType = FullVerticalImage;
        break;
      case "Icon Card":
        ComponentType = IconCard;
        break;
      case "Blog Card":
        ComponentType = BlogCard;
        break;
    }
  }

  // console.log("ComponentRenderer:", {
  //   Component_Master_Code: component.Component_Master_Code,
  //   Component_Name: component.Component_Name,
  //   ComponentType: ComponentType?.name || "Fallback",
  // });

  if (!ComponentType) {
    // Fallback to generic component rendering with full settings integration
    return (
      <Box
        className={`component-${component.Component_Master_Code?.toLowerCase()}`}
        sx={{
          // Apply component settings as styles
          mb: componentSettings.margin ? 0 : 2,
          padding:
            componentSettings.padding ||
            (componentSettings.backgroundColor ? 2 : 0),
          margin: componentSettings.margin || "0 0 16px 0",
          backgroundColor: componentSettings.backgroundColor || "transparent",
          color: componentSettings.textColor || themeStyles.primaryText,
          borderRadius: componentSettings.borderRadius || "0px",
          textAlign: componentSettings.textAlign || "left",
          fontSize: componentSettings.fontSize || "inherit",
          fontWeight: componentSettings.fontWeight || "normal",
          width: componentSettings.fullWidth ? "100%" : "auto",
          opacity: componentSettings.disabled ? 0.6 : 1,
          boxShadow:
            componentSettings.elevation > 0
              ? `0px ${componentSettings.elevation}px ${
                  componentSettings.elevation * 2
                }px rgba(0,0,0,0.12)`
              : "none",
          // Parse and apply custom styles
          ...(componentSettings.customStyles
            ? (() => {
                try {
                  return typeof componentSettings.customStyles === "string"
                    ? JSON.parse(componentSettings.customStyles)
                    : componentSettings.customStyles;
                } catch {
                  return {};
                }
              })()
            : {}),
        }}
      >
        {/* Primary Text (Main Heading) */}
        {component.Primary_Text && component.Primary_Text !== "NA" && (
          <Box sx={{ mb: 1 }}>
            <h4
              style={{
                color: componentSettings.textColor || themeStyles.primaryText,
                margin: "0 0 8px 0",
                fontSize:
                  componentSettings.size === "large"
                    ? "1.5rem"
                    : componentSettings.size === "small"
                    ? "1rem"
                    : "1.25rem",
                fontWeight: componentSettings.fontWeight || "600",
                textAlign: componentSettings.textAlign || "left",
              }}
            >
              {component.Primary_Text}
            </h4>
          </Box>
        )}

        {/* Secondary Text (Sub Heading) */}
        {component.Secondary_Text && (
          <Box sx={{ mb: 1 }}>
            <h5
              style={{
                color: componentSettings.textColor || themeStyles.secondaryText,
                margin: "0 0 8px 0",
                fontSize:
                  componentSettings.size === "large"
                    ? "1.2rem"
                    : componentSettings.size === "small"
                    ? "0.9rem"
                    : "1rem",
                fontWeight: componentSettings.fontWeight || "400",
                textAlign: componentSettings.textAlign || "left",
              }}
            >
              {component.Secondary_Text}
            </h5>
          </Box>
        )}

        {/* Descriptive Text (Main Content) */}
        {component.Descriptive_Text && (
          <Box sx={{ mb: 2 }}>
            <p
              style={{
                color: componentSettings.textColor || themeStyles.primaryText,
                margin: "0",
                lineHeight: 1.6,
                fontSize:
                  componentSettings.fontSize ||
                  (componentSettings.size === "large"
                    ? "1.1rem"
                    : componentSettings.size === "small"
                    ? "0.875rem"
                    : "1rem"),
                fontWeight: componentSettings.fontWeight || "normal",
                textAlign: componentSettings.textAlign || "left",
              }}
            >
              {component.Descriptive_Text}
            </p>
          </Box>
        )}

        {/* List Content (JSON Array) */}
        {component.List_Content && renderListContent(component.List_Content)}

        {/* Media Files (Array Handling) */}
        {component.Media_Files && (
          <Box sx={{ mt: 2, mb: 2 }}>
            {(() => {
              try {
                let mediaFiles;
                if (typeof component.Media_Files === "string") {
                  mediaFiles = JSON.parse(component.Media_Files);
                } else {
                  mediaFiles = component.Media_Files;
                }

                // Handle both array and object formats
                const mediaArray = Array.isArray(mediaFiles)
                  ? mediaFiles
                  : Object.values(mediaFiles);

                return mediaArray.map((url, index) => (
                  <img
                    key={index}
                    src={
                      url.startsWith("http")
                        ? url
                        : `${process.env.REACT_APP_BASEURL || ""}${url}`
                    }
                    alt={`${component.Primary_Text || "Component"} media ${
                      index + 1
                    }`}
                    style={{
                      maxWidth: componentSettings.fullWidth ? "100%" : "300px",
                      height: "auto",
                      marginRight: "10px",
                      marginBottom: "10px",
                      borderRadius: componentSettings.borderRadius || "8px",
                      border: darkMode
                        ? `1px solid ${theme.palette.grey[600]}`
                        : "1px solid #e0e0e0",
                      boxShadow:
                        componentSettings.elevation > 0
                          ? `0px ${componentSettings.elevation}px ${
                              componentSettings.elevation * 2
                            }px rgba(0,0,0,0.1)`
                          : "none",
                    }}
                  />
                ));
              } catch (e) {
                console.error("Error parsing Media_Files:", e);
                return null;
              }
            })()}
          </Box>
        )}

        {/* CTA Buttons */}
        {(component.Primary_CTA || component.Secondary_CTA) &&
          renderCTAButtons(component.Primary_CTA, component.Secondary_CTA)}
      </Box>
    );
  }

  // Render using specific component type with enhanced props
  return (
    <ComponentType
      component={component}
      section={section}
      settings={componentSettings}
      darkMode={darkMode}
      theme={theme}
      themeStyles={themeStyles}
      renderListContent={renderListContent}
      renderCTAButtons={renderCTAButtons}
    />
  );
}
